import { CircularProgress, Container } from '@mui/material';
import { Tooltip, Box } from '@mui/material';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { determineMediaState, MediaStates } from '../../../commands/practitioner/enums/MediaStates';
import { MediaTypes } from '../../../commands/practitioner/enums/MediaTypes';
import { PlaybackType } from '../../../commands/practitioner/enums/PlaybackType';
import useAppContext from '../../../hooks/useAppContext/useAppContext';
import { SourceProjection } from '../../../commands/practitioner/enums/SourceProjection';
import usePractitionerControlsContext from '../../../hooks/usePractitionerControlsContext/usePractitionerControlsContext';
import BackendService from '../../../services/BackendService';
import { startTimer, mediaKey } from '../../../metrics/sentryMetrics';
import ConfirmModal from '../../ConfirmModal/ConfirmModal';
import { Border } from '../ToggleVideoEMDR/ToggleVideoEMDR';
import './MediaControls.scss';
import useMediaContext from '../../../hooks/useMediaContextHook/useMediaContext';
import Play from '../../../assets/mainControls/IconPlay.svg?react';
import Pause from '../../../assets/mediaControls/IconPause.svg?react';
import Load from '../../../assets/mediaControls/multi-media.svg?react';
import Visible from '../../../assets/mediaControls/visible.svg?react';
import Invisible from '../../../assets/mediaControls/invisible.svg?react';
import Muted from '../../../assets/mediaControls/muted.svg?react';
import Unmuted from '../../../assets/mediaControls/unmuted.svg?react';
import ErrorDialog from '../../ErrorDialog/ErrorDialog';
import { TwilioError } from 'twilio-video';
import useCurrentSessionState from '../../../hooks/useCurrentSessionState/useCurrentSessionState';
import { commandVersion1_2_0, isVersionLessThan } from '../../../utils/versionCheck';

interface MediaControlsProps {
  modalIsOpened: boolean;
  setModalIsOpened: Dispatch<SetStateAction<boolean>>;
  setConnectionReminder?: Dispatch<SetStateAction<boolean>>;
}

const safeDecodeURI = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const stripQuotes = (value: string): string => value.replace(/^\\?"/, '').replace(/\\?"$/, '').trim();

// All plausible spellings of the same URI: raw, quote-stripped, and up to two
// decode passes — the command serializer percent-encodes and wraps the URI in
// \"..\" quotes, and the backend may echo any of these forms back.
const candidateMediaUriForms = (value: unknown): string[] => {
  if (typeof value !== 'string') return [];
  const forms = new Set<string>();
  let current = value.trim();
  for (let pass = 0; pass < 3; pass++) {
    forms.add(stripQuotes(current));
    const decoded = safeDecodeURI(current);
    if (decoded === current) break;
    current = decoded;
  }
  forms.delete('');
  return [...forms];
};

const isSameMediaUri = (reported: unknown, expected: string): boolean => {
  // An empty expected URI must never match — reset states also carry ''.
  const expectedForms = candidateMediaUriForms(expected);
  if (expectedForms.length === 0) return false;
  const reportedForms = candidateMediaUriForms(reported);
  return expectedForms.some((form) => reportedForms.includes(form));
};

const MediaControls: React.FC<MediaControlsProps> = ({ modalIsOpened, setModalIsOpened, setConnectionReminder }) => {
  const { sessionId, treatmentId, hasDevice } = useAppContext();
  const {
    practitionerMediaURI,
    setPractitionerMediaURI,
    isMediaLoaded,
    isConfirming,
    setIsConfirming,
    isPractitionerPlayerVisible,
    setIsPractitionerPlayerVisible,
    isPractitionerReactPlayerStarted,
    setIsPractitionerReactPlayerStarted,
    hasBeenSaved,
    setHasBeenSaved,
    taskHasStarted,
    setTaskHasStarted,
    practitionerMediaType,
    setIsMutedYoutube,
    isMutedYoutube,
    setSavedUri,
    projectionType,
    startResetTimeout,
    setStartResetTimeout,
    practitionerVideoTime,
  } = usePractitionerControlsContext();
  const { t } = useTranslation();
  const { setDiveIndex, setSelectedItemURL, setFirstDiveInd } = useMediaContext();
  const currentState = useCurrentSessionState() || {};
  const { isConnected: isDeviceConnected } = currentState as any;
  const tooltipUpload = t('upload-media');
  const tooltipEditMedia = t('edit-media');
  const tooltipShowMedia = t('show-media');
  const tooltipHideMedia = t('hide-media');
  const tooltipStart = t('start-media');
  const tooltipPause = t('pause-media');
  const tooltipStartDisabled = t('no-available-media');
  const [error, setError] = useState<string>('');
  const isMediaUriAvailable = Boolean(isMediaLoaded && hasBeenSaved);
  const basePath = 'assets/icons';
  const muteIcon = isMutedYoutube ? `${basePath}/muted.svg` : `${basePath}/unmuted.svg`;
  const prevConnectedRef = useRef<boolean | undefined>(undefined);
  const [isOnline, setIsonline] = useState<boolean>(navigator.onLine);
  const mediaTypeTitle =
    practitionerMediaType === MediaTypes.VIDEO
      ? 'video'
      : practitionerMediaType === MediaTypes.AUDIO
        ? 'audio'
        : 'image';

  const startTooltip =
    isMediaUriAvailable && practitionerMediaType !== MediaTypes.IMAGE
      ? isPractitionerReactPlayerStarted
        ? tooltipPause
        : tooltipStart
      : tooltipStartDisabled;
  const soundTooltip =
    isMediaUriAvailable && (practitionerMediaType === MediaTypes.VIDEO || practitionerMediaType === MediaTypes.AUDIO)
      ? !isMutedYoutube
        ? t('mute')
        : t('unmute')
      : t('noSoundOptions');

  const handleOpenModal = async () => {
    if (typeof document !== 'undefined') {
      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLElement) {
        activeElement.blur();
      }
    }
    if (isPractitionerPlayerVisible) {
      setIsConfirming(true);
      return;
    }
    await resetAllPreviousData();
    setHasBeenSaved(false);
    setModalIsOpened((prevState) => !prevState);
    setPractitionerMediaURI('');
  };

  const isOldVersion =
    typeof currentState.version === 'string' && isVersionLessThan(currentState.version, commandVersion1_2_0);

  const resetAllPreviousData = async () => {
    await BackendService.sendMediaStatusReport(sessionId, treatmentId, '', 0, 0);
    await BackendService.sendLoadMedia(sessionId, treatmentId, '', practitionerMediaType, projectionType, 'Off');
  };

  const handleConfirm = async () => {
    // Clear any old media that was loaded
    await BackendService.sendMediaStatusReport(sessionId, treatmentId, '', 0, 0);
    await BackendService.sendLoadMedia(sessionId, treatmentId, '', MediaTypes.VIDEO, SourceProjection.Flat, 'Off');
    await BackendService.sendMediaVisibility(sessionId, treatmentId, practitionerMediaURI, false);
    await BackendService.sendMediaCommand(sessionId, treatmentId, practitionerMediaURI, PlaybackType.Pause, 0);
    setHasBeenSaved(false);
    setIsConfirming(false);
    setModalIsOpened(true);
    setIsPractitionerPlayerVisible(false);
    setIsPractitionerReactPlayerStarted(false);
    setPractitionerMediaURI('');
    setIsMutedYoutube(true);
    setSavedUri('');
    setDiveIndex([]);
    setSelectedItemURL('');
    setFirstDiveInd(true);
  };

  const handleVisibleClicked = async () => {
    if (!isMediaUriAvailable || practitionerMediaType === MediaTypes.AUDIO) {
      return null;
    }
    const newVisibilityState = !isPractitionerPlayerVisible;
    setIsPractitionerPlayerVisible(newVisibilityState);
    await BackendService.sendMediaVisibility(sessionId, treatmentId, practitionerMediaURI, newVisibilityState);

    if (isPractitionerReactPlayerStarted) {
      await BackendService.sendMediaCommand(sessionId, treatmentId, practitionerMediaURI, PlaybackType.Pause, 0);
      setIsPractitionerReactPlayerStarted(false);
    }
  };

  const handleStart = async () => {
    if (!isMediaUriAvailable || practitionerMediaType === MediaTypes.IMAGE) {
      return;
    }
    const newStartedState = !isPractitionerReactPlayerStarted;
    setIsPractitionerReactPlayerStarted(newStartedState);
    if (sessionId && treatmentId && practitionerMediaURI && hasBeenSaved) {
      // jump to synchronized player.
      if (newStartedState && practitionerVideoTime > 0) {
        await BackendService.sendMediaCommand(
          sessionId,
          treatmentId,
          practitionerMediaURI,
          PlaybackType.Jump,
          practitionerVideoTime
        );
      }

      const playbackType = newStartedState ? PlaybackType.Play : PlaybackType.Pause;
      if (newStartedState) {
        // Start E2E timer; will be ended when client reports MLSR:Successful
        startTimer(mediaKey(practitionerMediaURI));
      }
      await BackendService.sendMediaCommand(sessionId, treatmentId, practitionerMediaURI, playbackType, 0);
      if (newStartedState) {
        await BackendService.sendMediaVisibility(sessionId, treatmentId, practitionerMediaURI, true);
        setIsPractitionerPlayerVisible(true);
      }
      if (taskHasStarted && hasDevice && isOldVersion) {
        setTimeout(() => {
          setTaskHasStarted(false);
        }, 150);
      }
    }
  };

  const isLoadingMedia = useMemo(() => {
    if (!practitionerMediaURI || !isMediaLoaded || !hasBeenSaved) return false;
    return startResetTimeout;
  }, [practitionerMediaURI, isMediaLoaded, hasBeenSaved, startResetTimeout]);

  useEffect(() => {
    // fired any time network status changes
    const updateStatus = () => setIsonline(navigator.onLine);

    window.addEventListener('online', updateStatus);
    window.addEventListener('offline', updateStatus);

    // call once in case the tab was opened while offline
    updateStatus();

    return () => {
      window.removeEventListener('online', updateStatus);
      window.removeEventListener('offline', updateStatus);
    };
  }, []);

  useEffect(() => {
    if (!startResetTimeout) return;

    let cancelled = false;
    let lastErrorMessage = '';

    /** Re-use this for any early-exit error case */
    const handleError = async (msg: string) => {
      await resetAllPreviousData();
      setPractitionerMediaURI('');
      setError(msg);
      setStartResetTimeout(false);
    };

    /* RETURN if offline */
    if (!isOnline) {
      handleError(t('user-is-offline'));
      return;
    }

    const resetTimeout = setTimeout(() => {
      void handleError(lastErrorMessage || t('no-load-response'));
    }, 31_000);

    /* POLL UNTIL LOAD COMPLETE.
       Fetch the state directly (1s, only while a load cycle is active) and only
       accept a report that carries the URI of the media we just sent — a stale
       Successful/Failure from a previous load belongs to a different URI, so it
       can neither complete nor kill this cycle. */
    let inFlight = false;
    const interval = setInterval(async () => {
      if (inFlight || cancelled) return;
      inFlight = true;
      try {
        const state: any = await BackendService.getCurrentState(sessionId, treatmentId);
        if (cancelled || !state) return;
        if (typeof state.errorMessage === 'string' && state.errorMessage) {
          lastErrorMessage = state.errorMessage;
        }
        if (!isSameMediaUri(state.mediaURI, practitionerMediaURI)) return;

        const reported = determineMediaState(Number(state.mediaState));
        if (reported === MediaStates.Successful) {
          clearTimeout(resetTimeout);
          clearInterval(interval);
          setStartResetTimeout(false);
        } else if (reported === MediaStates.Failure) {
          // Stop polling immediately on failure and show the specific error when present
          clearTimeout(resetTimeout);
          clearInterval(interval);
          void handleError(state.errorMessage || t('no-load-response'));
        }
      } catch {
        // transient network error — keep polling until the 31s fallback
      } finally {
        inFlight = false;
      }
    }, 1_000);

    return () => {
      cancelled = true;
      clearTimeout(resetTimeout);
      clearInterval(interval);
    };
  }, [startResetTimeout, isOnline, practitionerMediaURI, sessionId, treatmentId]);

  useEffect(() => {
    // Hide the practitioner preview when the glasses drop while media is active.
    //
    // Deliberately local-only: nothing is pushed to the client here. `pushClientCommand`
    // appends to a queue the headset drains on its next poll, so a command sent while it
    // is disconnected is not cleanup — it is delivered at the moment the headset comes
    // back. Resetting the media here used to unload the video on a headset that had been
    // healthy all along, and nothing restored it afterwards.
    //
    // The media stays loaded on the device, so the practitioner can resume playback once
    // the connection recovers. Opening the media modal still calls `resetAllPreviousData`,
    // which is what keeps re-selecting the same media working.
    if (!hasDevice) return;

    const wasConnected = prevConnectedRef.current;
    prevConnectedRef.current = isDeviceConnected;

    if (wasConnected === undefined) return;

    const connectionDropped = wasConnected && !isDeviceConnected;
    if (!connectionDropped) return;

    const mediaActive =
      isPractitionerReactPlayerStarted || isPractitionerPlayerVisible || (!!practitionerMediaURI && hasBeenSaved);

    if (mediaActive) {
      setIsPractitionerPlayerVisible(false);
      setConnectionReminder?.(true);
    }
  }, [isDeviceConnected, hasDevice]);

  return (
    <Border>
      {error ? <ErrorDialog dismissError={() => setError('')} error={new Error(error) as TwilioError} /> : null}
      <ConfirmModal
        title={t('confirm-title')}
        message={t('confirm-message')}
        open={isConfirming}
        closeAction={() => setIsConfirming(false)}
        confirmAction={handleConfirm}
      />
      <div className={'media-controls-container'}>
        <Container className="detail-container">
          <div className={'media-controls-options-container container-label'}>
            <h2>{t('media')} </h2>
            <h2 className={'media-type'}> {t(`load-play ${mediaTypeTitle}`)}</h2>
          </div>
          <div className={'media-controls-options-container'}>
            <div className="tooltip-container" onClick={handleOpenModal} data-testid="pwid-media-load">
              <Tooltip title={tooltipEditMedia}>
                <span className="span">
                  <button>
                    <Load className="load icon-sign" />
                  </button>
                </span>
              </Tooltip>
            </div>
            {isLoadingMedia && practitionerMediaType !== MediaTypes.IMAGE ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginRight: '16px' }}>
                <CircularProgress style={{ color: '#AFC1DD' }} />
              </Box>
            ) : (
              practitionerMediaType !== MediaTypes.IMAGE && (
                <div
                  className={`tooltip-container ${!isMediaUriAvailable || !hasBeenSaved ? 'isDisabled' : ''}`}
                  onClick={handleStart}
                  data-testid="pwid-media-play"
                >
                  <Tooltip title={startTooltip}>
                    <span className="span">
                      <button disabled={!isMediaUriAvailable || !hasBeenSaved}>
                        {!isPractitionerReactPlayerStarted ? (
                          <Play className="icon-sign" />
                        ) : (
                          <Pause className="icon-sign" />
                        )}
                      </button>
                    </span>
                  </Tooltip>
                </div>
              )
            )}
            {isLoadingMedia && practitionerMediaType !== MediaTypes.AUDIO ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginRight: '16px' }}>
                <CircularProgress style={{ color: '#AFC1DD' }} />
              </Box>
            ) : (
              practitionerMediaType !== MediaTypes.AUDIO && (
                <div
                  className={`tooltip-container ${!isMediaUriAvailable ? 'isDisabled' : ''}`}
                  onClick={handleVisibleClicked}
                  data-testid="pwid-media-visibility"
                >
                  <Tooltip title={isPractitionerPlayerVisible ? tooltipHideMedia : tooltipShowMedia}>
                    <span className="span">
                      <button disabled={!isMediaUriAvailable}>
                        {' '}
                        {isPractitionerPlayerVisible ? (
                          <Visible className={'visible icon-sign'} />
                        ) : (
                          <Invisible className={'hide icon-sign'} />
                        )}
                      </button>
                    </span>
                  </Tooltip>
                </div>
              )
            )}
            {isLoadingMedia && practitionerMediaType !== MediaTypes.IMAGE ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginRight: '16px' }}>
                <CircularProgress style={{ color: '#AFC1DD' }} />
              </Box>
            ) : (
              practitionerMediaType !== MediaTypes.IMAGE && (
                <div
                  className={`tooltip-container ${!isMediaUriAvailable || !hasBeenSaved ? 'isDisabled' : ''}`}
                  data-testid="pwid-media-mute"
                >
                  <Tooltip
                    title={soundTooltip}
                    placement="top"
                    onClick={() => setIsMutedYoutube((prevState) => !prevState)}
                  >
                    <span className="span">
                      <button disabled={!hasBeenSaved && !isMediaUriAvailable}>
                        {isMutedYoutube ? (
                          <Muted className="muted icon-sign" />
                        ) : (
                          <Unmuted className="unmuted icon-sign" />
                        )}
                      </button>
                    </span>
                  </Tooltip>
                </div>
              )
            )}
          </div>
        </Container>
      </div>
    </Border>
  );
};

export default MediaControls;
